import { useMemo, useState, type ReactNode } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { ActionKey, DocumentDef } from '@project/contracts'
import { Button, Field, Input, Modal, toast, type MenuItem } from '../design/index.js'
import { useApi, type DocumentDetail, type LineRow } from '../app/api.js'
import { navigate, paths } from '../app/router.js'
import { messageFor } from './feedback.js'
import type { DocumentForm } from './FormTab.js'
import { NtBoqPanel, isNtLine, withoutNt } from './NtBoqPanel.js'

/**
 * The Action ▾ entries a document offers beyond its workflow (reference:
 * an approved project's Save NT-BOQ · Update · Download Bills · Mark as
 * Closed, and Delete on a discarded draft).
 *
 * Kept out of DocumentPage, which only spreads what this returns into its
 * menu and draws the dialog and the extra panel it hands back.
 */
export interface DocumentExtras {
  readonly items: readonly MenuItem[]
  /** Workflow actions this document shows elsewhere (a project revises from its Revisions tab). */
  readonly hidden: ReadonlySet<ActionKey>
  /** The approved form's `editableAfterApproval` fields are open. */
  readonly approvedEditable: boolean
  /** The lines the main form shows — an approved project's NT work has its own panel. */
  readonly formLines: (lines: readonly LineRow[]) => readonly LineRow[]
  readonly formFooter: ReactNode
  readonly dialog: ReactNode
  /** What Cancel is called right now: a draft that never went anywhere is discarded. */
  readonly cancelLabel: string
}

type Confirm = 'close' | 'reopen' | 'delete' | 'extend' | null

export const useDocumentExtras = ({
  def, doc, editing, form, companyId, onError,
}: {
  def: DocumentDef | undefined
  doc: DocumentDetail | undefined
  editing: boolean
  form: DocumentForm
  companyId: string
  onError: (message: string | null) => void
}): DocumentExtras => {
  const api = useApi()
  const client = useQueryClient()
  const [confirm, setConfirm] = useState<Confirm>(null)
  const [closedOn, setClosedOn] = useState(() => new Date().toISOString().slice(0, 10))
  const [reason, setReason] = useState('')
  const [returnBy, setReturnBy] = useState('')
  const [ntLines, setNtLines] = useState<readonly LineRow[] | null>(null)

  const docKey = def?.key ?? ''
  const id = doc?.id ?? ''
  const status = doc?.documentStatus
  const isProject = docKey === 'project'
  const approved = status === 'APPROVED' && !editing
  const editableAfterApproval = useMemo(
    () => (def?.form.sections ?? []).flatMap((s) => s.fields).filter((f) => f.editableAfterApproval),
    [def],
  )

  const landed = (saved: DocumentDetail) => {
    client.setQueryData(['doc', api.companyId, docKey, id], saved)
    void client.invalidateQueries({ queryKey: ['list', api.companyId, docKey] })
    onError(null)
  }

  const run = useMutation({
    mutationFn: async (what: 'close' | 'reopen' | 'update' | 'ntBoq' | 'delete' | 'extend') => {
      switch (what) {
        case 'extend': return api.extendToolReturn(id, { returnBy, reason: reason.trim() || null })
        case 'close': return api.closeProject(id, { closedOn, reason: reason.trim() || null })
        case 'reopen': return api.reopenProject(id)
        case 'update': {
          const fields: Record<string, unknown> = {}
          for (const f of editableAfterApproval) fields[f.key] = form.getValues(f.key) ?? null
          return api.updateHeader(docKey, id, { version: doc?.version, fields })
        }
        case 'ntBoq':
          return api.saveNtBoq(id, { version: doc?.version, lines: ntLines ?? (doc?.lines ?? []).filter(isNtRelated(doc?.lines ?? [])) })
        case 'delete':
          await api.deleteDocument(docKey, id)
          return null
      }
    },
    onSuccess: (saved, what) => {
      setConfirm(null)
      setReason('')
      if (what === 'delete' || saved === null) {
        void client.invalidateQueries({ queryKey: ['list', api.companyId, docKey] })
        // Said on the list it lands on: the toast outlives the page (Toast.tsx).
        toast('The document has been deleted successfully!')
        if (def) navigate(paths.list(companyId, def.route))
        return
      }
      if (what === 'ntBoq') setNtLines(null)
      if (what === 'update') form.reset(saved.fields)
      landed(saved)
    },
    onError: (err) => onError(messageFor(err)),
  })

  // Never submitted: unnumbered, or numbered on its first save (PROJ3-001).
  const wentNowhere = doc !== undefined && (!doc.documentNo || doc.neverSubmitted === true)
  const items: MenuItem[] = []
  if (doc && def) {
    if (isProject && approved) {
      items.push({ key: 'saveNtBoq', label: 'Save NT-BOQ', onSelect: () => run.mutate('ntBoq') })
    }
    if (approved && editableAfterApproval.length > 0) {
      items.push({ key: 'updateHeader', label: 'Update', onSelect: () => run.mutate('update') })
    }
    if (isProject && approved) {
      items.push({
        key: 'downloadBills', label: 'Download bills',
        onSelect: () => window.location.assign(api.projectBillsUrl(id)),
      })
      items.push(doc.project?.ongoing === false
        ? { key: 'reopenProject', label: 'Reopen project', onSelect: () => setConfirm('reopen') }
        : { key: 'markClosed', label: 'Mark as closed', onSelect: () => setConfirm('close') })
    }
    // FIX-4: tools out longer than planned — a special action, refused by the
    // server to anyone the company has not given it to.
    if (docKey === 'tool_issue' && approved && doc.fields['issueType'] !== 'RETURN') {
      items.push({
        key: 'extendReturnDate', label: 'Extend return date',
        onSelect: () => { setReturnBy(typeof doc.fields['returnBy'] === 'string' ? doc.fields['returnBy'] : ''); setConfirm('extend') },
      })
    }
    // Reference: a discarded draft's menu offers only Delete.
    if (status === 'CANCELLED' && wentNowhere) {
      items.push({ key: 'delete', label: 'Delete', danger: true, onSelect: () => setConfirm('delete') })
    }
  }

  const hidden = new Set<ActionKey>(isProject && editableAfterApproval.length > 0 ? ['update'] : [])

  const dialog = confirm && doc ? (
    <Modal
      title={confirm === 'extend' ? 'Extend the return date' : confirm === 'close' ? 'Mark this project as closed?'
        : confirm === 'reopen' ? 'Reopen this project?'
          : 'Deleting document?'}
      tone={confirm === 'delete' ? 'warn' : 'plain'}
      onClose={() => setConfirm(null)}
      actions={(
        <>
          <Button kind="ghost" onClick={() => setConfirm(null)}>
            {confirm === 'delete' ? 'No, don’t delete it' : 'Keep it as it is'}
          </Button>
          <Button
            kind="primary"
            disabled={run.isPending || (confirm === 'close' && closedOn === '') || (confirm === 'extend' && returnBy === '')}
            onClick={() => run.mutate(confirm)}
          >
            {confirm === 'extend' ? 'Extend' : confirm === 'close' ? 'Mark as closed' : confirm === 'reopen' ? 'Reopen' : 'Yes, delete it'}
          </Button>
        </>
      )}
    >
      {confirm === 'extend' ? (
        <>
          <p>The tools stay with the same person; only the day they are due back moves. The change is recorded.</p>
          <div className="field-grid" style={{ marginTop: 18 }}>
            <Field label="Due back on" required>
              <Input type="date" value={returnBy} onChange={setReturnBy} />
            </Field>
            <Field label="Why?">
              <Input value={reason} onChange={setReason} placeholder="Work on site runs another week" />
            </Field>
          </div>
        </>
      ) : confirm === 'close' ? (
        <>
          <p>
            The project stops counting as ongoing: the dashboards and the “ongoing only” reports leave it out.
            Everything raised against it stays, and Reopen undoes this.
          </p>
          <div className="field-grid" style={{ marginTop: 18 }}>
            <Field label="Closed on" required>
              <Input type="date" value={closedOn} onChange={setClosedOn} />
            </Field>
            <Field label="Why is it closed?">
              <Input value={reason} onChange={setReason} placeholder="Handed over, final bill raised" />
            </Field>
          </div>
        </>
      ) : confirm === 'reopen' ? (
        <p>The project counts as ongoing again, and its closing date is cleared.</p>
      ) : (
        <p>Deleted documents are permanently removed. This one was discarded before it was ever submitted.</p>
      )}
    </Modal>
  ) : null

  const formFooter = isProject && approved && doc ? (
    <NtBoqPanel
      doc={doc}
      lines={ntLines ?? (doc.lines ?? []).filter(isNtRelated(doc.lines ?? []))}
      dirty={ntLines !== null}
      busy={run.isPending}
      onChange={setNtLines}
      onSave={() => run.mutate('ntBoq')}
    />
  ) : null

  return {
    items,
    hidden,
    approvedEditable: approved && editableAfterApproval.length > 0,
    formLines: isProject && approved ? withoutNt : (lines) => lines,
    formFooter,
    dialog,
    cancelLabel: status === 'DRAFT' && wentNowhere ? 'Discard' : 'Cancel document',
  }
}

/** An NT BOQ line, or an estimate under one. */
const isNtRelated = (all: readonly LineRow[]) => {
  const nt = new Set(all.filter(isNtLine).map((l) => String(l['id'])))
  return (l: LineRow): boolean =>
    isNtLine(l) || (l['section'] === 'ESTIMATION' && nt.has(String(l['parentLineId'] ?? '')))
}
